import { useState, useEffect, useRef } from "react";
import { X, Check } from "lucide-react";

/**
 * PDFTextAnnotation - Modal for editing text annotation content
 */
export default function PDFTextAnnotation({
  annotation,
  onSave,
  onCancel,
  onDelete,
}) {
  const [text, setText] = useState(annotation?.text || "");
  const textareaRef = useRef(null);

  useEffect(() => {
    // Focus and select text on open
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, []);

  const handleSave = () => {
    if (text.trim()) {
      onSave(annotation.id, { text: text.trim() });
    } else {
      // Delete if text is empty
      onDelete(annotation.id);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      onCancel();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={onCancel}
    >
      <div 
        className="bg-white rounded-xl shadow-xl p-4 w-full max-w-sm mx-4"
        onClick={e => e.stopPropagation()}
        data-testid="text-annotation-editor"
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-[#1A1B1A]">Edit Text</h3>
          <button
            onClick={onCancel}
            className="p-1 text-[#757675] hover:text-[#1A1B1A] rounded"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <textarea
          ref={textareaRef}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter text..."
          rows={3}
          className="w-full px-3 py-2 border border-[#E5E5E3] rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#205B5A] focus:border-transparent"
          data-testid="text-annotation-input"
        />

        <p className="text-[10px] text-[#757675] mt-1 mb-3">
          Press Enter to save, Shift+Enter for new line, Escape to cancel
        </p>

        <div className="flex items-center justify-between">
          <button
            onClick={() => onDelete(annotation.id)}
            className="text-xs text-red-500 hover:text-red-600"
            data-testid="delete-text-annotation"
          >
            Delete
          </button>
          <div className="flex gap-2">
            <button
              onClick={onCancel}
              className="px-3 py-1.5 text-xs text-[#757675] hover:bg-[#FAF9F6] rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-3 py-1.5 text-xs bg-[#205B5A] text-white rounded-lg hover:bg-[#174544] flex items-center gap-1"
              data-testid="save-text-annotation"
            >
              <Check className="w-3 h-3" />
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
